import { ReactNode, useEffect, useState } from 'react'
import useAssetsLoading from '../hooks/useAssetsLoading.ts'
import style from './AssetsGate.module.scss'

// Images from the cache or a fast connection arrive within a few frames; a loader drawn for that long
// only flickers. Under this delay the loader takes its place but stays invisible.
export const LOADER_DELAY_MS = 300
// A loader that did appear stays long enough to read as a loader, not a flash, when the images arrive
// just after the delay.
export const LOADER_MIN_VISIBLE_MS = 400

interface IAssetsGateProps {
  // The URLs the content draws, so the browser serves them from its cache.
  urls: Array<string | null | undefined>
  // The store's loader in the content's shape, so nothing moves when the content arrives. Without one
  // the content is only held back, e.g. a banner that waits together with the grid below it.
  loader?: ReactNode
  children: ReactNode
}

// Holds a block back until its images have loaded, failed or run out of time, and shows the store's
// loader meanwhile. Every block waiting for images goes through here, so the delay and the minimum time
// on screen are the same in every store.
const AssetsGate = ({ urls, loader = null, children }: IAssetsGateProps) => {
  const loading = useAssetsLoading(urls)
  // When the loader became visible; null while it is invisible or gone.
  const [shownAt, setShownAt] = useState<number>(null)

  useEffect(() => {
    if (loading) {
      if (null !== shownAt) return
      const timer = setTimeout(() => setShownAt(Date.now()), LOADER_DELAY_MS)
      return () => clearTimeout(timer)
    }
    if (null === shownAt) return
    const timer = setTimeout(() => setShownAt(null), Math.max(0, shownAt + LOADER_MIN_VISIBLE_MS - Date.now()))
    return () => clearTimeout(timer)
  }, [loading, shownAt])

  if (!loading && null === shownAt) return <>{children}</>
  if (!loader) return null

  // `display: contents` leaves the loader's own box in the parent's layout; `visibility` is inherited,
  // so the invisible loader still takes its place.
  return (
    <div className={`${style.gate} ${null === shownAt ? style.invisible : ''}`} data-testid="SW-EC-AssetsGate">
      {loader}
    </div>
  )
}

export default AssetsGate
